/**
 * What an action's map symbol says about it.
 *
 * The layer used to draw every action in the same cyan whether it was closed or
 * hours overdue, so the map showed where work was and nothing else. Three
 * facets now carry meaning: status is the colour, priority is the size, and
 * being overdue is a red outline.
 *
 * The facets are collapsed into a single string key because clustering requires
 * a FeatureLayer, and a FeatureLayer symbolises through a renderer rather than
 * per-graphic symbols. A `UniqueValueRenderer` over the full cross product of
 * keys gets per-feature symbology back without giving up clustering. Keeping
 * that cross product here, in plain data, is also what makes it testable.
 */

import {
  ACTION_PRIORITY_VALUES,
  ACTION_STATUS_VALUES,
  isActionOverdue,
} from '../actionsTable/actionEditModel';

export type Rgba = [number, number, number, number];

export type ActionSymbolSpec = {
  color: Rgba;
  /** Marker diameter in points; polygons ignore it. */
  size: number;
  outlineColor: Rgba;
  outlineWidth: number;
};

export type ActionSymbolFacets = {
  status: string;
  priority: string;
  overdue: boolean;
  /**
   * A chosen `UscgSymbol` key. Absent, null or '' all mean the plain status
   * circle, which is still the common case.
   *
   * Not validated here: the catalog lives in `uscgSymbology.ts`, which pulls in
   * ArcGIS, and this module stays free of it so the cross product can be tested
   * in node. Callers validate before they get here — see `useActionLayerSync`.
   */
  symbology?: string | null;
};

export const ACTION_SYMBOL_KEY_FIELD = 'symbolKey';

const UNKNOWN_STATUS = 'Unknown';

/** Status is the primary read, so it gets hue. */
const STATUS_COLOR: Record<string, Rgba> = {
  Planned: [96, 165, 250, 0.9],
  'Current/Ongoing': [251, 191, 36, 0.95],
  Blocked: [251, 113, 133, 0.95],
  'Submitted for Review': [167, 139, 250, 0.95],
  Approved: [52, 211, 153, 0.95],
  Closed: [74, 222, 128, 0.75],
  [UNKNOWN_STATUS]: [148, 163, 184, 0.9],
};

/** Priority is a magnitude, so it gets size. */
const PRIORITY_SIZE: Record<string, number> = {
  Low: 8,
  Normal: 11,
  High: 15,
  Critical: 19,
};

const OVERDUE_OUTLINE: Rgba = [239, 68, 68, 1];
const DEFAULT_OUTLINE: Rgba = [255, 255, 255, 1];

const KEY_SEPARATOR = '|';

export function actionSymbolFacets(
  action: {
    status?: string | null;
    priority?: string | null;
    target_date?: Date | string | null;
    symbology?: string | null;
  },
  now: Date = new Date(),
  /**
   * Already checked against the catalog by the caller; '' and null both mean
   * "use the derived circle".
   */
  symbology: string | null = null
): ActionSymbolFacets {
  const status =
    action.status && ACTION_STATUS_VALUES.includes(action.status)
      ? action.status
      : UNKNOWN_STATUS;
  const priority =
    action.priority && ACTION_PRIORITY_VALUES.includes(action.priority)
      ? action.priority
      : 'Normal';
  return {
    status,
    priority,
    overdue: isActionOverdue(action.status, action.target_date, now),
    symbology: symbology || null,
  };
}

/** The derived part of the key, without the symbology prefix. */
const DERIVED_SYMBOLOGY = '_';

export function actionSymbolKey(facets: ActionSymbolFacets): string {
  return [
    facets.symbology || DERIVED_SYMBOLOGY,
    facets.status,
    facets.priority,
    facets.overdue ? 'overdue' : 'ontime',
  ].join(KEY_SEPARATOR);
}

export function actionSymbolSpec(facets: ActionSymbolFacets): ActionSymbolSpec {
  return {
    color: STATUS_COLOR[facets.status] ?? STATUS_COLOR[UNKNOWN_STATUS],
    size: PRIORITY_SIZE[facets.priority] ?? PRIORITY_SIZE.Normal,
    outlineColor: facets.overdue ? OVERDUE_OUTLINE : DEFAULT_OUTLINE,
    // A thicker ring is what makes overdue readable at cluster-level zoom,
    // where a colour change alone disappears.
    outlineWidth: facets.overdue ? 3 : 1.5,
  };
}

/** The polygon form of a spec: translucent fill, the status colour as the edge. */
export function actionPolygonSpec(
  facets: ActionSymbolFacets
): ActionSymbolSpec {
  const spec = actionSymbolSpec(facets);
  const [r, g, b] = spec.color;
  return {
    color: [r, g, b, 0.25],
    size: spec.size,
    outlineColor: facets.overdue ? OVERDUE_OUTLINE : [r, g, b, 1],
    outlineWidth: facets.overdue ? 3 : 2,
  };
}

/**
 * The derived symbols the renderer always has to know about.
 *
 * A Closed action is never overdue by definition, so those combinations are
 * left out rather than generated and never matched.
 */
export function actionSymbolKeys(): ActionSymbolFacets[] {
  const statuses = [...ACTION_STATUS_VALUES, UNKNOWN_STATUS];
  return statuses.flatMap((status) =>
    ACTION_PRIORITY_VALUES.flatMap((priority) =>
      (status === 'Closed' ? [false] : [false, true]).map((overdue) => ({
        status,
        priority,
        overdue,
        symbology: null,
      }))
    )
  );
}

/**
 * The full set of symbols a renderer needs for a given screenful of actions.
 *
 * The derived 26 are always present so an action without a chosen symbol never
 * depends on what else happens to be on screen. Chosen symbols are added only
 * where they are actually used: the full cross product would be forty symbols
 * times twenty-six states, which is a renderer nobody needs.
 */
export function actionSymbolKeysInUse(
  facetsInUse: readonly ActionSymbolFacets[]
): ActionSymbolFacets[] {
  const combined = actionSymbolKeys();
  const seen = new Set(combined.map(actionSymbolKey));
  facetsInUse.forEach((facets) => {
    if (!facets.symbology) return;
    const key = actionSymbolKey(facets);
    if (seen.has(key)) return;
    seen.add(key);
    combined.push(facets);
  });
  return combined;
}

/** Human-readable legend, one row per status plus the overdue and size rules. */
export function actionSymbolLegend(): {
  label: string;
  spec: ActionSymbolSpec;
}[] {
  return [
    ...[...ACTION_STATUS_VALUES, UNKNOWN_STATUS].map((status) => ({
      label: status,
      spec: actionSymbolSpec({
        status,
        priority: 'Normal',
        overdue: false,
        symbology: null,
      }),
    })),
    {
      label: 'Overdue',
      spec: actionSymbolSpec({
        status: 'Current/Ongoing',
        priority: 'Normal',
        overdue: true,
        symbology: null,
      }),
    },
    ...ACTION_PRIORITY_VALUES.map((priority) => ({
      label: `${priority} priority`,
      spec: actionSymbolSpec({
        status: 'Planned',
        priority,
        overdue: false,
        symbology: null,
      }),
    })),
  ];
}

/**
 * What a chosen symbol looks like, for the legend.
 *
 * Kept as copy rather than forty rows of icons: the ring is the part that still
 * carries meaning, and the icons speak for themselves.
 */
export const ACTION_SYMBOLOGY_LEGEND_NOTE =
  'A task with a chosen symbol shows that icon inside the same ring, so its status, priority and overdue state still read the same way.';
